import { useRef } from "react";

import {
  defaultWidth,
  PANE_LIMITS,
  QUEUE_MIN,
  useConsoleLayout,
} from "../../stores/console";

/**
 * The divider between two console columns, which is also a handle.
 *
 * ## It is one pixel wide and that is fine
 *
 * The line the host sees is the same 1px rule the layout already had; what is
 * grabbable is an invisible strip either side of it, which is why the hit area
 * and the visible line are two different elements. A divider thick enough to
 * grab comfortably is thick enough to look like a gutter, and the console has
 * three columns already competing for the eye.
 *
 * ## Pointer events, not mouse events
 *
 * `setPointerCapture` is what makes a drag survive the pointer leaving the
 * element - which it does immediately, because the element is two pixels wide
 * and the hand is moving. Without capture the drag stops the moment the cursor
 * outruns the handle, which feels like the divider slipping out of your grip.
 *
 * It also gets the trackpad and the touchscreen for free.
 *
 * ## Why the width is not written on every frame
 *
 * It is - to the store, sixty times a second, because the column has to follow
 * the pointer. What is not written sixty times a second is `localStorage`:
 * `writeStored` coalesces, so a drag costs one write when the host lets go.
 */
export function PaneResizer({
  pane,
  /**
   * Which way widening goes.
   *
   * The studio is left of its divider, so dragging right makes it wider. The
   * rail is right of its own, so dragging right makes it narrower. Getting this
   * backwards produces a handle that runs away from the pointer.
   */
  direction,
  /** The grid, measured to keep the middle column usable. */
  containerRef,
  label,
}: {
  pane: "studioWidth" | "railWidth";
  direction: 1 | -1;
  containerRef: React.RefObject<HTMLDivElement | null>;
  label: string;
}) {
  const width = useConsoleLayout((s) => s[pane]);
  const other = useConsoleLayout((s) =>
    pane === "studioWidth" ? s.railWidth : s.studioWidth,
  );
  const setWidth = useConsoleLayout((s) => s.setWidth);

  const drag = useRef<{ x: number; from: number } | null>(null);
  const self = useRef<HTMLDivElement | null>(null);

  const limits = PANE_LIMITS[pane];

  /**
   * How wide this column may actually get right now.
   *
   * Its own ceiling, or whatever leaves the queue `QUEUE_MIN` after the other
   * side has taken its share - whichever is smaller. Without this a host on a
   * laptop can drag the studio until the queue is a sliver, and the only way
   * back is to drag a divider they can no longer see.
   *
   * The other side's width is read live rather than measured, because a column
   * still using the responsive default has no stored number to read.
   */
  const ceiling = () => {
    const container = containerRef.current?.clientWidth ?? 0;

    if (container === 0) return limits.max;

    // The other column's stored width, or - when it has never been dragged -
    // whatever its `clamp()` resolves to at this container width.
    const taken =
      other ??
      defaultWidth(
        pane === "studioWidth" ? "railWidth" : "studioWidth",
        container,
      );

    return Math.max(limits.min, Math.min(limits.max, container - taken - QUEUE_MIN));
  };

  const apply = (next: number) =>
    setWidth(pane, Math.max(limits.min, Math.min(ceiling(), Math.round(next))));

  const begin = (event: React.PointerEvent<HTMLDivElement>) => {
    // The rendered width, so a drag that starts from the responsive default
    // continues from where the column actually is rather than jumping to a
    // number it never had.
    const from =
      width ??
      (pane === "studioWidth"
        ? (self.current?.previousElementSibling as HTMLElement | null)
            ?.offsetWidth
        : (self.current?.nextElementSibling as HTMLElement | null)
            ?.offsetWidth) ??
      limits.min;

    drag.current = { x: event.clientX, from };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const move = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;

    apply(drag.current.from + (event.clientX - drag.current.x) * direction);
  };

  const end = (event: React.PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  /*
   * Arrow keys, because this is a control.
   *
   * A separator that can only be operated by dragging is one that cannot be
   * operated without a pointer, and the console is a working surface somebody
   * may well be driving from the keyboard. `role="separator"` with a tabindex
   * is the documented pattern, and browsers announce it as a splitter.
   */
  const key = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 48 : 12;
    const current = width ?? limits.min;

    if (event.key === "ArrowLeft") apply(current - step * direction);
    else if (event.key === "ArrowRight") apply(current + step * direction);
    else if (event.key === "Home" || event.key === "End") setWidth(pane, null);
    else return;

    event.preventDefault();
  };

  return (
    /*
     * This element *is* the divider - it is the 1px grid track, and the grab
     * strip inside it is what the pointer actually meets. Hovering the strip
     * hovers this too, because CSS `:hover` applies to ancestors, so the line
     * can highlight itself without a `group` or a second coloured element.
     *
     * Hidden below `lg`, where the console stacks into one column and a
     * vertical divider between things no longer side by side would be a handle
     * for resizing nothing.
     */
    <div
      ref={self}
      className="relative hidden bg-line transition-colors hover:bg-accent focus-within:bg-accent lg:block"
      data-pane-resizer={pane}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={width ?? undefined}
        aria-valuemin={limits.min}
        aria-valuemax={limits.max}
        tabIndex={0}
        onPointerDown={begin}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onKeyDown={key}
        // Back to the responsive default, which is otherwise unreachable once a
        // number has been stored.
        onDoubleClick={() => setWidth(pane, null)}
        title={`${label} — drag to resize, double-click to reset`}
        /*
         * Wider than the line it sits on, and centred over it: four pixels of
         * grab either side without occupying any layout width. `touch-none` so
         * a drag on a touchscreen resizes rather than scrolling the console out
         * from under the finger.
         */
        className="absolute inset-y-0 -inset-x-1 z-10 cursor-col-resize touch-none outline-none"
      />
    </div>
  );
}
